import { createContext, useEffect, useMemo } from 'react';
import { reducer } from './cardPerfReducer';
import { middleware } from './cardPerfMiddleware';
import { Provider, createStoreHook, createDispatchHook, createSelectorHook } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { getIsInitialLoadSettled } from './perfLogger';

// This is a fallback store location that is used when a store is not provided.
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const FALLBACK_STORE_LOCATION = 'FALLBACK_REDUX_STORE';
const initializeStore = (preloadedState, options = {}) => configureStore({
  reducer,
  preloadedState,
  middleware: getDefaultMiddleware =>
  // If perf logging is disabled remove all logging middleware
  (options === null || options === void 0 ? void 0 : options.disablePerfMetricLogging) === true ? getDefaultMiddleware() : getDefaultMiddleware().concat(middleware)
});

// Using an IIFE to create a context with a default value.
// IIFE is so we don't have keep a copy of the fallback store in memory.
const StoreContext = (() => {
  const fallbackStore = configureStore({
    reducer,
    preloadedState: {
      location: FALLBACK_STORE_LOCATION,
      isInitialLoadSettled: false,
      loggingTag: 'FALLBACK_LOCATION_STATE_REDUX_STORE',
      isResolvedLocation: false,
      isVirtualizationEnabled: false,
      timings: {
        locationRenderStartedMs: performance.now()
      },
      dataByCard: {},
      cardTypesLoaded: [],
      changedTabsBeforeInitialLoadSettled: false
    },
    middleware: getDefaultMiddleware => getDefaultMiddleware().concat(middleware)
  });

  // createContext requeries both a store and a storeState, even if you
  // give it a preloadedState.
  return /*#__PURE__*/createContext({
    store: fallbackStore,
    storeState: fallbackStore.getState()
  });
})();
export const useStore = createStoreHook(StoreContext);
export const useDispatch = createDispatchHook(StoreContext);
export const useSelector = createSelectorHook(StoreContext);
export function LocationStateContextProvider({
  disablePerfMetricLogging,
  renderStartedAtMs,
  location,
  loggingTag,
  isVirtualizationEnabled,
  children
}) {
  var _parentStore$getState;
  // If there is a store present high in the tree, use it instead of creating a new one
  const parentStore = useStore();
  const localStore = useMemo(() => initializeStore({
    location,
    isInitialLoadSettled: getIsInitialLoadSettled(location, loggingTag),
    loggingTag,
    totalCardCount: 0,
    isVirtualizationEnabled,
    isResolvedLocation: false,
    timings: {
      locationRenderStartedMs: renderStartedAtMs
    },
    dataByCard: {},
    cardTypesLoaded: [],
    changedTabsBeforeInitialLoadSettled: false
  }, {
    disablePerfMetricLogging
  }), [disablePerfMetricLogging, isVirtualizationEnabled, location, loggingTag, renderStartedAtMs]);
  useEffect(() => {
    // clean up remaining timeouts on unmount
    return () => {
      try {
        const {
          dataByCard
        } = localStore.getState();
        Object.values(dataByCard).forEach(cardState => {
          if (cardState.timeoutId) {
            clearTimeout(cardState.timeoutId);
          }
        });
      } catch (e) {
        // no-op this is a cleanup function
      }
    };
  }, [parentStore, localStore]);
  if (((_parentStore$getState = parentStore.getState()) === null || _parentStore$getState === void 0 ? void 0 : _parentStore$getState.location) !== FALLBACK_STORE_LOCATION) {
    parentStore.dispatch({
      type: 'NESTED_CARD_PERF_CONTEXT_INITIALIZED',
      startedAtMs: performance.now()
    });
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(Provider, {
    context: StoreContext,
    store: localStore,
    children: children
  });
}